import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flag, MapPin, Trophy, CalendarDays } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getRaces, getRaceSeasons } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function Races() {
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState("");
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, dateLocale } = useLang();

  // load available seasons dynamically
  useEffect(() => {
    getRaceSeasons()
      .then((data) => {
        const list = (data || []).map(String);
        setSeasons(list);
        if (list.length > 0) {
          setSeason((s) => s || list[0]);
        } else {
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!season) return;
    setLoading(true);
    getRaces(Number(season))
      .then(setRaces)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [season]);

  return (
    <PageWrap testId="races-page">
      <section className="carbon-bg relative overflow-hidden pt-36 pb-12">
        <div className="speed-lines absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("races.kicker")}</div>
            <h1 data-testid="races-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("races.title")}
            </h1>
          </motion.div>

          <div className="mt-8 flex items-center gap-3">
            <CalendarDays className="h-4 w-4 text-white/40" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">{t("races.season")}</span>
            <Select value={season} onValueChange={setSeason}>
              <SelectTrigger data-testid="races-filter-season-select" className="h-10 w-32 border-white/15 bg-[#0F1217] font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0F1217]">
                {seasons.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`races-season-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* timeline */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        {loading ? (
          <div className="space-y-4" data-testid="races-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="clip-corner h-28 animate-pulse border border-white/5 bg-[#0F1217]" />
            ))}
          </div>
        ) : (
          <div data-testid="races-timeline" className="relative space-y-4">
            {/* vertical rail */}
            <div className="absolute bottom-0 left-[7px] top-0 hidden w-[2px] bg-gradient-to-b from-[#1EA7FF]/50 via-white/10 to-[#B7FF2A]/40 md:block" />
            {races.map((r, i) => {
              const isDone = r.status === "done";
              return (
                <motion.div
                  key={r.id}
                  data-testid={`race-event-card-${i}`}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                  className="relative md:pl-10"
                >
                  {/* dot */}
                  <span
                    className={`absolute left-0 top-8 hidden h-4 w-4 rounded-full border-2 md:block ${
                      isDone
                        ? "border-[#1EA7FF] bg-[#1EA7FF]/20"
                        : "border-[#B7FF2A] bg-[#B7FF2A]/20 pulse-acid"
                    }`}
                  />
                  <div
                    className={`light-sweep clip-corner group grid grid-cols-1 gap-4 border bg-[#0F1217] p-5 transition-colors duration-200 sm:grid-cols-12 sm:items-center ${
                      r.highlight ? "border-[#1EA7FF]/35" : "border-white/10"
                    } ${isDone ? "hover:border-[#1EA7FF]/50" : "hover:border-[#B7FF2A]/50"}`}
                  >
                    {/* date */}
                    <div className="sm:col-span-2">
                      <div className="font-mono text-3xl font-semibold text-white">
                        {new Date(r.date).toLocaleDateString(dateLocale, { day: "2-digit" })}
                      </div>
                      <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/45">
                        {new Date(r.date).toLocaleDateString(dateLocale, { month: "short", year: "numeric" })}
                      </div>
                    </div>
                    {/* name */}
                    <div className="sm:col-span-5">
                      <div className="font-display text-2xl uppercase tracking-[0.04em] leading-tight sm:text-3xl">
                        {r.name}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-sm text-white/50">
                        <MapPin className="h-3.5 w-3.5" /> {r.location} · {r.country}
                      </div>
                    </div>
                    {/* series */}
                    <div className="sm:col-span-2">
                      <Badge className="rounded-sm border border-white/15 bg-white/5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 hover:bg-white/5">
                        {r.series}
                      </Badge>
                    </div>
                    {/* result */}
                    <div className="sm:col-span-3 sm:text-right">
                      {isDone ? (
                        <span
                          data-testid={`race-result-${i}`}
                          className="inline-flex items-center gap-2 bg-[#1EA7FF]/10 px-3 py-1.5 font-mono text-sm font-semibold uppercase tracking-[0.1em] text-[#1EA7FF]"
                        >
                          <Trophy className="h-4 w-4" /> {r.result}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 bg-[#B7FF2A]/10 px-3 py-1.5 font-mono text-sm font-semibold uppercase tracking-[0.1em] text-[#B7FF2A]">
                          <Flag className="h-4 w-4" /> {t("races.upcoming")}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
            {races.length === 0 && (
              <div className="py-16 text-center text-white/40" data-testid="races-empty-state">
                {t("races.empty")}
              </div>
            )}
          </div>
        )}
      </section>
    </PageWrap>
  );
}
